import React, { useState, useEffect } from 'react'
import {
  Box,
  Grid,
  GridItem,
  HStack,
  Text,
  Button,
  Icon,
  Skeleton,
  Progress,
} from '@chakra-ui/react'
import {
  FiClock,
  FiShare2,
  FiStar,
  FiArrowRight,
  FiActivity,
} from 'react-icons/fi'
import { useNavigate } from 'react-router-dom'
import { useRecentlyViewed } from '@/hooks/useRecentlyViewed'
import { useFavorites } from '@/contexts/FavoritesContext'
import { useAppTranslation } from '@/contexts/LanguageContext'
import apiService from '@/services/api'

function useRelativeTime(dateString: string | undefined) {
  if (!dateString) return ''
  const now = new Date()
  const date = new Date(dateString)
  const diffMs = now.getTime() - date.getTime()
  const diffMin = Math.floor(diffMs / 60000)
  if (diffMin < 1) return 'just now'
  if (diffMin < 60) return `${diffMin} min ago`
  const diffHours = Math.floor(diffMin / 60)
  if (diffHours < 24) return `${diffHours}h ago`
  const diffDays = Math.floor(diffHours / 24)
  return `${diffDays}d ago`
}

export default function QuickAccessCards() {
  const navigate = useNavigate()
  const { t } = useAppTranslation(['files', 'common'])
  const { items: recentItems } = useRecentlyViewed()
  const { favorites } = useFavorites()

  const [sharedCount, setSharedCount] = useState<number | null>(null)
  const [usagePercent, setUsagePercent] = useState<number | null>(null)

  useEffect(() => {
    apiService
      .getSharedWithMe()
      .then((files) => setSharedCount(files.length))
      .catch(() => setSharedCount(0))

    apiService
      .getUsageSummary()
      .then((data) => {
        const entries = Object.entries(data.features).filter(
          ([, stats]) => stats.percentage !== null
        )
        if (entries.length > 0) {
          const [, highest] = entries.reduce((a, b) =>
            (a[1].percentage ?? 0) > (b[1].percentage ?? 0) ? a : b
          )
          setUsagePercent(Math.round(highest.percentage ?? 0))
        } else {
          setUsagePercent(0)
        }
      })
      .catch(() => setUsagePercent(0))
  }, [])

  const latestFile = recentItems?.[0]
  const relativeTime = useRelativeTime(latestFile?.viewed_at)

  return (
    <Box px={{ base: 4, md: 8 }} pb={6}>
      <Grid
        templateColumns={{ base: '1fr', lg: '1fr 1fr 1fr 1fr' }}
        templateRows={{ base: 'auto', lg: 'auto auto' }}
        gap={4}
      >
        {/* Continue where you left off -- left half, spans 2 rows */}
        <GridItem colSpan={{ base: 1, lg: 2 }} rowSpan={{ base: 1, lg: 2 }}>
          <Box
            bg="blue.50"
            borderRadius="xl"
            p={6}
            h="full"
            minH="160px"
            display="flex"
            flexDirection="column"
            justifyContent="space-between"
            border="1px solid"
            borderColor="blue.100"
            transition="box-shadow 0.2s"
            _hover={{ shadow: 'md' }}
          >
            <Box>
              <Box
                bg="blue.500"
                w={10}
                h={10}
                borderRadius="lg"
                display="flex"
                alignItems="center"
                justifyContent="center"
                mb={4}
              >
                <Icon as={FiClock} color="white" boxSize={5} />
              </Box>
              <Text fontWeight="bold" fontSize="lg" color="gray.800" mb={1}>
                {t('dashboard.continueWhereYouLeftOff')}
              </Text>
              {latestFile ? (
                <Text fontSize="sm" color="gray.600">
                  "{latestFile.file.display_name || latestFile.file.original_name}"{' '}
                  {t('dashboard.lastEdited', { time: relativeTime })}
                </Text>
              ) : (
                <Text fontSize="sm" color="gray.500">
                  {t('dashboard.noRecentFiles')}
                </Text>
              )}
            </Box>
            {latestFile && (
              <Button
                variant="outline"
                colorScheme="blue"
                size="sm"
                mt={4}
                w="fit-content"
                onClick={() => navigate(`/preview/${latestFile.file.id}`)}
                borderRadius="lg"
              >
                {t('dashboard.resumeSession')}
              </Button>
            )}
          </Box>
        </GridItem>

        {/* Shared with Me -- top-right, first cell */}
        <GridItem>
          <Box
            bg="purple.50"
            borderRadius="xl"
            p={5}
            h="full"
            border="1px solid"
            borderColor="purple.100"
            cursor="pointer"
            transition="all 0.2s"
            _hover={{ shadow: 'md', borderColor: 'purple.200' }}
            onClick={() => navigate('/shared-with-me')}
          >
            <HStack spacing={3}>
              <Box
                bg="purple.500"
                w={8}
                h={8}
                borderRadius="lg"
                display="flex"
                alignItems="center"
                justifyContent="center"
                flexShrink={0}
              >
                <Icon as={FiShare2} color="white" boxSize={4} />
              </Box>
              <Box flex={1}>
                <Text fontWeight="semibold" fontSize="sm" color="gray.800">
                  {t('navigation.sharedWithMe', { ns: 'common' })}
                </Text>
                {sharedCount !== null ? (
                  <Text fontSize="xs" color="gray.500">
                    {t('dashboard.sharedDescription', { count: sharedCount })}
                  </Text>
                ) : (
                  <Skeleton h="14px" w="120px" mt={1} />
                )}
              </Box>
            </HStack>
          </Box>
        </GridItem>

        {/* Starred -- top-right, second cell */}
        <GridItem>
          <Box
            bg="yellow.50"
            borderRadius="xl"
            p={5}
            h="full"
            border="1px solid"
            borderColor="yellow.100"
            cursor="pointer"
            transition="all 0.2s"
            _hover={{ shadow: 'md', borderColor: 'yellow.200' }}
            onClick={() => navigate('/favorites')}
          >
            <HStack spacing={3}>
              <Box
                bg="yellow.500"
                w={8}
                h={8}
                borderRadius="lg"
                display="flex"
                alignItems="center"
                justifyContent="center"
                flexShrink={0}
              >
                <Icon as={FiStar} color="white" boxSize={4} />
              </Box>
              <Box flex={1}>
                <Text fontWeight="semibold" fontSize="sm" color="gray.800">
                  {t('dashboard.starred')}
                </Text>
                <Text fontSize="xs" color="gray.500">
                  {t('dashboard.starredDescription', { count: favorites.length })}
                </Text>
              </Box>
            </HStack>
          </Box>
        </GridItem>

        {/* View Usage -- bottom-right, spans both right columns */}
        <GridItem colSpan={{ base: 1, lg: 2 }}>
          <Box
            bg="green.50"
            borderRadius="xl"
            p={5}
            border="1px solid"
            borderColor="green.100"
            cursor="pointer"
            transition="all 0.2s"
            _hover={{ shadow: 'md', borderColor: 'green.200' }}
            onClick={() => navigate('/account/usage')}
          >
            <HStack justify="space-between" align="center">
              <HStack spacing={3}>
                <Box
                  bg="green.500"
                  w={8}
                  h={8}
                  borderRadius="lg"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  flexShrink={0}
                >
                  <Icon as={FiActivity} color="white" boxSize={4} />
                </Box>
                <Box>
                  <Text fontWeight="semibold" fontSize="sm" color="gray.800">
                    {t('dashboard.viewUsage')}
                  </Text>
                  {usagePercent !== null ? (
                    <Text fontSize="xs" color="gray.500">
                      {t('dashboard.usageDescription', { percent: usagePercent })}
                    </Text>
                  ) : (
                    <Skeleton h="14px" w="160px" mt={1} />
                  )}
                </Box>
              </HStack>
              <Icon as={FiArrowRight} color="green.500" boxSize={5} />
            </HStack>
            {usagePercent !== null && (
              <Progress
                value={usagePercent}
                size="xs"
                colorScheme={usagePercent > 90 ? 'red' : usagePercent > 75 ? 'yellow' : 'green'}
                borderRadius="full"
                mt={3}
              />
            )}
          </Box>
        </GridItem>
      </Grid>
    </Box>
  )
}
